import { COLON_TRANSLATION_TABLE, isColonGrant } from './colon-grammar';
import type { NormalizationRejection, NormalizationResult, RawGrant } from './types';

/**
 * Step 1 of the engine (Addendum A.2): raw grants → dotted grammar.
 *
 * Deliberately NOT re-implemented from the legacy matcher: `forbiddenPermissions`, the `ui.*`
 * entry gate and the root-user bypass. The first was a TODO the legacy code itself flags, the
 * second is replaced by "the shell requires at least one module manifest to grant access", and
 * the third becomes an ordinary `*.*` grant.
 */

/** `domain.entity.action` / `domain.entity.*` / `domain.*` / `*.*` / `*.*.*` / `domain.*.action` */
const DOTTED_SEGMENT = /^[a-z0-9][a-z0-9-]*$|^\*$/;

const isWellFormedDotted = (grant: RawGrant): boolean => {
  if (grant.length === 0 || grant !== grant.trim()) return false;
  const segments = grant.split('.');
  if (segments.length < 2 || segments.length > 3) return false;
  return segments.every((segment) => DOTTED_SEGMENT.test(segment));
};

export function normalizeGrants(raw: readonly RawGrant[]): NormalizationResult {
  const grants: RawGrant[] = [];
  const rejected: NormalizationRejection[] = [];
  const seen = new Set<RawGrant>();

  const accept = (grant: RawGrant): void => {
    if (seen.has(grant)) return;
    seen.add(grant);
    grants.push(grant);
  };

  for (const grant of raw) {
    if (typeof grant !== 'string') {
      rejected.push({ grant: String(grant), reason: 'malformed' });
      continue;
    }
    if (isColonGrant(grant)) {
      const translated = COLON_TRANSLATION_TABLE[grant];
      if (translated === undefined) {
        rejected.push({ grant, reason: 'unknown-colon-grant' });
        continue;
      }
      for (const dotted of translated) accept(dotted);
      continue;
    }
    if (!isWellFormedDotted(grant)) {
      rejected.push({ grant, reason: 'malformed' });
      continue;
    }
    accept(grant);
  }

  return { grants, rejected };
}

/** Malformed grants are visible, never silent — the inspector and the console both show them. */
export function logRejections(
  result: NormalizationResult,
  log: Pick<Console, 'warn'> = console,
): void {
  if (result.rejected.length === 0) return;
  log.warn(
    `[permissions] ${result.rejected.length} grant(s) rejected during normalization:`,
    result.rejected.map((r) => `${r.grant} (${r.reason})`).join(', '),
  );
}
